import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import LoaderScreen from "../../../../components/layout/LoaderScreen";
import AxiosMain from "../../../../components/layout/AxiosMain";

const SelectNewNumber = (props) => {
  const { handleContinue, handleBack, products, data, t } = props;

  const [selectedNumbers, setSelectedNumbers] = useState({});
  const [addresses, setAddresses] = useState({});
  const [selectedMunicipalities, setSelectedMunicipalities] = useState({});
  const [municipalities, setMunicipalities] = useState({});
  const [availableNumbers, setAvailableNumbers] = useState({});
  const [errors, setErrors] = useState({});
  const [errorDisplay, setErrorDisplay] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState({});
  const [loadingNumbers, setLoadingNumbers] = useState({});

  // Handle address input change
  const handleAddressChange = (uniqueKey) => (event) => {
    setAddresses((prev) => ({
      ...prev,
      [uniqueKey]: event.target.value,
    }));
  };

  // Handle municipality selection
  const handleMunicipalityChange = (uniqueKey, productId) => async (event) => {
    const municipalityId = event.target.value;
    setSelectedMunicipalities((prev) => ({
      ...prev,
      [uniqueKey]: municipalityId,
    }));
    // Reset any previously selected number for this row
    setSelectedNumbers((prev) => ({
      ...prev,
      [uniqueKey]: null,
    }));
    // Clear error related to the number field
    setErrors((prev) => ({
      ...prev,
      [`number_${uniqueKey}`]: "",
    }));

    try {
      setLoadingNumbers((prev) => ({ ...prev, [uniqueKey]: true }));

      const response = await AxiosMain.get(
        `/numbers/product/${productId}/municipality/${municipalityId}/count/${filteredProducts.length}/available/numbers`
      );

      if (response.status === 204) {
        setAvailableNumbers((prev) => ({
          ...prev,
          [uniqueKey]: [],
        }));
        Swal.fire({
          text: "No number available",
          icon: "warning",
          title: "Warning",
        });
      } else {
        setAvailableNumbers((prev) => ({
          ...prev,
          [uniqueKey]: response.data,
        }));
      }
    } catch (error) {
      Swal.fire({
        text: "No number available",
        icon: "warning",
        title: "Warning",
      });
      console.error(
        `Error fetching numbers for product ${productId} and municipality ${municipalityId}:`,
        error
      );
      setAvailableNumbers((prev) => ({ ...prev, [uniqueKey]: [] }));
      setErrors((prev) => ({
        ...prev,
        [`number_${uniqueKey}`]: "Failed to load numbers.",
      }));
    } finally {
      setLoadingNumbers((prev) => ({ ...prev, [uniqueKey]: false }));
    }
  };

  // Handle number selection
  const handleNumberChange = (uniqueKey) => (event) => {
    const newNumberId = event.target.value;
    // Find the selected number object
    const selectedNumberObj = availableNumbers[uniqueKey]?.find(
      (num) => num.id === newNumberId
    );

    setSelectedNumbers((prev) => ({
      ...prev,
      [uniqueKey]: selectedNumberObj || null, // store full object {id, phone_number, ...}
    }));

    // Clear any error on the number field
    setErrors((prev) => ({
      ...prev,
      [`number_${uniqueKey}`]: "",
    }));
  };

  useEffect(() => {
    if (data.currentStep1 && data.currentStep2) {
      const prevData = new Set(data.currentStep1);

      const temp = data.currentStep2;
      const quantities = temp.quantities;
      const portings = temp.portings;
      let prod_list = [];
      let counter = 0;
      products.forEach((product) => {
        if (prevData.has(product.id) && product.porting_enable) {
          let diff = quantities[product.id] - portings[product.id];
          for (let i = 0; i < diff; ++i) {
            prod_list.push({
              ...product,
              uniqueKey: `${product.id}-${counter++}`,
            });
          }
        }
      });

      setFilteredProducts(prod_list);
    } else {
      setErrorDisplay("Something went wrong, please start again!");
    }
    if (data.currentStep3) {
      const currentData = data.currentStep3;
      setAddresses(currentData.addresses);
      setSelectedMunicipalities(currentData.selectedMunicipalities);
      setAvailableNumbers(currentData.availableNumbers || availableNumbers);
      setSelectedNumbers(currentData.selectedNumbers || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, products]);

  // Fetch municipalities for each filtered product
  useEffect(() => {
    const fetchMunicipalities = async () => {
      const municipalityData = {};
      const loadingData = {};
      try {
        const promises = filteredProducts.map(async (product) => {
          loadingData[product.uniqueKey] = true;
          const response = await AxiosMain.get(
            `/utilities/products/${product.id}/municipalities`
          );
          municipalityData[product.uniqueKey] = response.data;
          loadingData[product.uniqueKey] = false;
        });
        await Promise.all(promises);
        setMunicipalities(municipalityData);
        setLoadingMunicipalities(loadingData);
      } catch (error) {
        console.error("Error fetching municipalities:", error);
        setErrorDisplay(
          "Failed to load municipalities. Please try again later."
        );
      }
    };

    if (filteredProducts.length > 0) {
      fetchMunicipalities();
    }
  }, [filteredProducts]);

  // Fetch available numbers for each uniqueKey when data.currentStep3 is true
  // useEffect(() => {
  //   const fetchAvailableNumbersForStep3 = async () => {
  //     if (!data.currentStep3) return;

  //     const uniqueKeys = filteredProducts.map((product) => product.uniqueKey);
  //     const numberFetchPromises = uniqueKeys.map(async (uniqueKey) => {
  //       const product = filteredProducts.find(
  //         (prod) => prod.uniqueKey === uniqueKey
  //       );
  //       const municipalityId = selectedMunicipalities[uniqueKey];
  //       if (municipalityId && product) {
  //         try {
  //           setLoadingNumbers((prev) => ({ ...prev, [uniqueKey]: true }));
  //           const response = await AxiosMain.get(
  //             `/numbers/product/${product.id}/municipality/${municipalityId}/count/${filteredProducts.length}/available/numbers`
  //           );

  //           if (response.status === 204) {
  //             setAvailableNumbers((prev) => ({
  //               ...prev,
  //               [uniqueKey]: [],
  //             }));
  //             Swal.fire({
  //               text: t("NoNumbers"),
  //               icon: "warning",
  //               title: "Warning",
  //             });
  //           } else {
  //             setAvailableNumbers((prev) => ({
  //               ...prev,
  //               [uniqueKey]: response.data,
  //             }));
  //           }
  //         } catch (error) {
  //           if (error.response && error.response.status === 204) {
  //             setAvailableNumbers((prev) => ({
  //               ...prev,
  //               [uniqueKey]: [],
  //             }));
  //             Swal.fire({
  //               text: t("NoNumbers"),
  //               icon: "warning",
  //               title: "Warning",
  //             });
  //           } else {
  //             console.error(
  //               `Error fetching numbers for product ${product.id} and municipality ${municipalityId}:`,
  //               error
  //             );
  //             setAvailableNumbers((prev) => ({ ...prev, [uniqueKey]: [] }));
  //             setErrors((prev) => ({
  //               ...prev,
  //               [`number_${uniqueKey}`]: t("FailedToLoadNumbers"),
  //             }));
  //           }
  //         } finally {
  //           setLoadingNumbers((prev) => ({ ...prev, [uniqueKey]: false }));
  //         }
  //       }
  //     });

  //     await Promise.all(numberFetchPromises);
  //   };

  //   fetchAvailableNumbersForStep3();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [data.currentStep3, filteredProducts, selectedMunicipalities]);

  // Compute all selected number IDs for uniqueness
  const selectedNumberIds = useMemo(() => {
    // We assume selectedNumbers[uniqueKey] is an object { id, phone_number }
    return new Set(
      Object.values(selectedNumbers)
        .filter(Boolean)            // remove null/undefined
        .map((item) => item.id)    // map to id
    );
  }, [selectedNumbers]);

  // Validation before continue
  const validate = () => {
    let tempErrors = {};
    filteredProducts.forEach((product) => {
      const uniqueKey = product.uniqueKey;

      if (!addresses[uniqueKey] || addresses[uniqueKey].trim() === "") {
        tempErrors[`address_${uniqueKey}`] = t("AddressRequired");
      }
      if (!selectedMunicipalities[uniqueKey]) {
        tempErrors[`municipality_${uniqueKey}`] = t("MunicipalityRequired");
      }
      // If available numbers exist, ensure we have a selection
      if (
        availableNumbers[uniqueKey] &&
        availableNumbers[uniqueKey].length > 0
      ) {
        // Check if we have an object with .id
        if (!selectedNumbers[uniqueKey] || !selectedNumbers[uniqueKey].id) {
          tempErrors[`number_${uniqueKey}`] = t("NumberSelectionRequired");
        }
      }
    });
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Handle continue button
  const onContinue = () => {
    if (validate()) {
      // Pass along the entire object so we have both id and phone_number
      handleContinue({
        addresses,
        selectedMunicipalities,
        selectedNumbers,
        availableNumbers
      });
    } else {
      setErrorDisplay(t("FixErrors"));
    }
  };

  if (!filteredProducts.length && !errorDisplay) {
    return <LoaderScreen />;
  }

  return (
    <Box sx={{ p: 4, width: "65vw" }}>
      {errorDisplay && (
        <Typography color="error" variant="h6">
          {errorDisplay}
        </Typography>
      )}

      <Box>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {t("SelectNewNumbers")}
        </Typography>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "#4BABCC" }}>
              <TableRow>
                <TableCell>
                  <Typography color={"white"} fontWeight="bold">
                    {t("Product")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={"white"} fontWeight="bold">
                    {t("Address")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={"white"} fontWeight="bold">
                    {t("Municipality")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color={"white"} fontWeight="bold">
                    {t("Number")}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => {
                const uniqueKey = product.uniqueKey;
                const isNoNumbers =
                  availableNumbers[uniqueKey] &&
                  availableNumbers[uniqueKey].length === 0;

                return (
                  <TableRow key={uniqueKey}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      <TextField
                        id={`address-${uniqueKey}`}
                        label={t("Address")}
                        type="text"
                        name={"address"}
                        value={addresses[uniqueKey] || ""}
                        onChange={handleAddressChange(uniqueKey)}
                        variant="outlined"
                        size="small"
                        error={Boolean(errors[`address_${uniqueKey}`])}
                        helperText={errors[`address_${uniqueKey}`] || ""}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      {loadingMunicipalities[uniqueKey] ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <Select
                          value={selectedMunicipalities[uniqueKey] || ""}
                          onChange={handleMunicipalityChange(
                            uniqueKey,
                            product.product_id
                          )}
                          displayEmpty
                          sx={{ width: "100%" }}
                          error={Boolean(
                            errors[`municipality_${uniqueKey}`]
                          )}
                        >
                          <MenuItem value="" disabled>
                            {t("Select")}
                          </MenuItem>
                          {municipalities[uniqueKey] &&
                            municipalities[uniqueKey].map((municipality) => (
                              <MenuItem
                                key={municipality.id}
                                value={municipality.odoo_id}
                              >
                                {municipality.name}
                              </MenuItem>
                            ))}
                        </Select>
                      )}
                      {errors[`municipality_${uniqueKey}`] && (
                        <Typography color="error" variant="caption">
                          {errors[`municipality_${uniqueKey}`]}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {loadingNumbers[uniqueKey] ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : isNoNumbers ? (
                        <Typography variant="body2" color="textSecondary">
                          {t("NoNumbers")}
                        </Typography>
                      ) : (
                        <Select
                          // value = ID of the currently selected object, or ""
                          value={selectedNumbers[uniqueKey]?.id || ""}
                          onChange={handleNumberChange(uniqueKey)}
                          displayEmpty
                          sx={{ width: "100%" }}
                          disabled={
                            !selectedMunicipalities[uniqueKey] || isNoNumbers
                          }
                          error={Boolean(errors[`number_${uniqueKey}`])}
                        >
                          <MenuItem value="" disabled>
                            {t("Select")}
                          </MenuItem>
                          {availableNumbers[uniqueKey] &&
                            availableNumbers[uniqueKey]
                              .filter(
                                (number) =>
                                  // Not selected by others, or is currently selected by this row
                                  !selectedNumberIds.has(number.id) ||
                                  selectedNumbers[uniqueKey]?.id === number.id
                              )
                              .map((number) => (
                                <MenuItem key={number.id} value={number.id}>
                                  {number.phone_number}
                                </MenuItem>
                              ))}
                        </Select>
                      )}
                      {errors[`number_${uniqueKey}`] && (
                        <Typography color="error" variant="caption">
                          {errors[`number_${uniqueKey}`]}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Continue and Back Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="text"
            sx={{ color: "#00AEEF" }}
            onClick={handleBack}
          >
            {t("Back")}
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#00AEEF", color: "white" }}
            onClick={onContinue}
          >
            {t("Continue")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SelectNewNumber;
